import { faClone, faRightLeft } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, CircularProgress, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { ICampaign, IDuplicateFormData, getAllCampaignLanguages } from "campaign/model"
import {
  closeDuplicatePopup,
  duplicateCampaignOrForecast,
  getDefaultDuplicateForm,
  setDuplicatePopupIsSaving,
  validateDuplicateFormData
} from "campaign/tools/duplicate"
import { FieldDate } from "form/components/fields/FieldDate"
import { FieldSelect } from "form/components/fields/FieldSelect"
import { FieldSelectOrder } from "form/components/fields/FieldSelectOrder"
import { Form, Formik } from "formik"
import { messageSVC } from "message/service"
import { CAMPAIGN_SUMMARY_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { Popup } from "ui/components/Popup"
import { isPhoneScreen } from "ui/tools/responsive"

const END_DATE_FIELD_NAME: string = "endDate"

interface IDuplicatePopupProps {
  campaign: ICampaign | null
  isForecastConvert: boolean
  isSaving: boolean
  isPhone: boolean
}

const DuplicatePopupFn = React.memo(({ campaign, isForecastConvert, isSaving, isPhone }: IDuplicatePopupProps) => {
  if (!campaign) {
    return null
  }
  return (
    <Popup
      icon={isForecastConvert ? faRightLeft : faClone}
      id="duplicate-popup"
      displayCloseIcon={!isSaving}
      title={translate(isForecastConvert ? "forecast.convert.title" : "campaign.duplicate.title")}
      onClose={() => closeDuplicatePopup()}
      minWidth={isPhone ? "calc(100% - 2rem)" : "25rem"}
      maxWidth={isPhone ? "calc(100% - 2rem)" : "25rem"}>
      <FlexBox vertical fullWidth sx={{ pl: "1rem", pr: "1rem", pb: "1rem" }}>
        <Typography color="textPrimary" fontSize=".75rem" sx={{ maxWidth: "30rem", mt: ".5rem", mb: ".5rem" }}>
          {translate(isForecastConvert ? "forecast.convert.name" : "campaign.duplicate.name", campaign.product || "")}
        </Typography>
        <Formik
          initialValues={getDefaultDuplicateForm(campaign, isForecastConvert)}
          validate={(values: IDuplicateFormData) => validateDuplicateFormData(false, false, values)}
          onSubmit={(values, actions) => {
            setDuplicatePopupIsSaving(true)
            duplicateCampaignOrForecast(campaign, isForecastConvert, values)
              .then(newCampaign => {
                if (newCampaign === null) {
                  // user cancel confirmation
                  setDuplicatePopupIsSaving(false)
                  actions.setSubmitting(false)
                  return
                } else {
                  closeDuplicatePopup()
                }
                navigate({ path: CAMPAIGN_SUMMARY_PATH, pathParams: newCampaign.id })
              })
              .catch(error => {
                console.error(error)
                messageSVC.error(
                  translate(isForecastConvert ? "forecast.convert.error.unknown" : "campaign.duplicate.error.unknown")
                )
                actions.setSubmitting(false)
                setDuplicatePopupIsSaving(false)
              })
          }}>
          {({ errors, touched, values, isSubmitting }) => {
            const canSubmit =
              !isSubmitting && Object.keys(errors).length === 0 && isForecastConvert
                ? Object.keys(touched).length > 0
                : true
            return (
              <Form style={{ width: "100%" }}>
                <FlexBox
                  wrap
                  gap
                  fullWidth
                  sx={{
                    "height": "100%",
                    "minHeight": "100%",
                    "maxHeight": "100%",
                    "& > *": {
                      width: "20rem"
                    }
                  }}>
                  <FieldSelectOrder
                    orderFieldName="order_id"
                    advertiserFieldName="advertiser_id"
                    error={errors.order_id}
                    sx={{ width: "20rem" }}
                    sxFirstField={{
                      "justifyContent": "start",
                      "width": "100%",
                      "flexWrap": "nowrap",
                      "& > div:first-of-type": {
                        width: "100%"
                      },
                      "& > button": { width: "10rem" }
                    }}
                    sxSecondField={{ width: "100%" }}
                  />
                  <FieldSelect
                    fieldName="language_id"
                    label={translate("label.language")}
                    value={values.language_id}
                    options={getAllCampaignLanguages()}
                    optionLabelFn={locale => translate(`label.language.${locale}`)}
                    error={errors.language_id}
                  />
                  <FlexBox vertical gap sx={{ "& > *": { width: "100%" } }}>
                    <FieldDate
                      fieldName="startDate"
                      label={translate("label.startDate")}
                      value={values.startDate}
                      error={errors.startDate}
                      required
                      toTouchFieldName={END_DATE_FIELD_NAME}
                    />
                    <FieldDate
                      fieldName={END_DATE_FIELD_NAME}
                      label={translate("label.endDate")}
                      value={values.endDate}
                      error={errors.endDate}
                      required
                    />
                  </FlexBox>
                </FlexBox>
                <FlexBox fullWidth ai="end" jc="end" sx={{ mt: "2rem" }}>
                  <Button
                    variant="contained"
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress sx={{ color: theme => theme.palette.primary.main }} size="1.5rem" />
                      ) : isForecastConvert ? (
                        <FontAwesomeIcon icon={faRightLeft} />
                      ) : (
                        <FontAwesomeIcon icon={faClone} />
                      )
                    }
                    type="submit"
                    disabled={!canSubmit || isSaving}>
                    {translate(isForecastConvert ? "button.create" : "button.duplicate")}
                  </Button>
                </FlexBox>
              </Form>
            )
          }}
        </Formik>
      </FlexBox>
    </Popup>
  )
})

export const DuplicatePopup = connect(
  (state: IRootState): IDuplicatePopupProps => ({
    campaign: state.campaign.duplicatePopup.campaign,
    isForecastConvert: state.campaign.duplicatePopup.isForecastConvert,
    isSaving: state.campaign.duplicatePopup.isSaving,
    isPhone: isPhoneScreen(state)
  })
)(DuplicatePopupFn)
